import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import {
  Demographic,
  DemographicOption,
  GeographicCountry,
} from "@/types/resources";
import { DemographicsResponse, GeographicsResponse } from "@/types/responses";

const useGetDemographics = () => {
  const [demographics, setDemographics] = useState<
    Demographic<Exclude<DemographicOption, "kids_age">>[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient("/metadata");

  const fetchDemographics = useCallback(() => {
    client
      .get<DemographicsResponse>("/demographics")
      .then((response) => {
        setDemographics(response.data.demographics);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchDemographics();
  }, [fetchDemographics]);

  useEffect(() => {
    fetchDemographics();
  }, [fetchDemographics]);

  return {
    demographics,
    error,
    loading,
    refresh,
  };
};

const useGetGeographics = () => {
  const [geographics, setGeographics] = useState<GeographicCountry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient("/metadata");

  const fetchGeographics = useCallback(() => {
    client
      .get<GeographicsResponse>("/geographics")
      .then((response) => {
        setGeographics(response.data.geographics);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchGeographics();
  }, [fetchGeographics]);

  useEffect(() => {
    fetchGeographics();
  }, [fetchGeographics]);

  return { error, geographics, loading, refresh };
};

export { useGetDemographics, useGetGeographics };
